import { ColumnDef, Column, Row } from '@tanstack/react-table';
import { useSearchParams } from 'react-router-dom';
import useTheme from 'chat-ui/src/hooks/useTheme';
import { IconKeyboard, IconVideo, IconVolume } from '@tabler/icons-react';
import UserFilledIcon from '@/components/icons/UserFilledIcon';
import TooltipWrapper from '@/components/tooltip/TooltipWrapper';
import replaceUnderscoreWithSpace from '@/utils/transformUnderscoreToSpace';
import useTranslation from '@/hooks/useTranslation';
import { stripMarkdown } from '@/utils/stringHelpers';
import { AllTranscriptsProps, TableSortingMethods } from '@/types';
import { TranscriptsView } from '@/enums';
import TranscriptsColumnsHeaderButton from './TranscriptsColumnsHeaderButton';
import TranscriptsColumnsCell from './TranscriptsColumnsCell';

const TranscriptsColumns = (
  metadataKeysArray: string[],
  activeView: TranscriptsView = TranscriptsView.COMPLETED,
) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { currentTheme } = useTheme();
  const { lang } = useTranslation();

  const addQueryParams = (params: Record<string, string>) => {
    Object.entries(params).forEach(([key, value]) => {
      searchParams.set(key, value);
    });
    setSearchParams(searchParams);
  };

  const getColor = (row) => {
    if (row?.original?.inRun === true && !row?.original?.language) return currentTheme?.primary;
    if (row?.original?.language) return '#808080';
    return '#ff8c00';
  };

  const toggleSortHandler = ({ toggleSorting, getIsSorted }: TableSortingMethods) =>
    toggleSorting(getIsSorted() === 'asc');

  const isScreenedOut = activeView === TranscriptsView.SCREENED_OUT;

  const columns: ColumnDef<AllTranscriptsProps>[] = [
    {
      accessorKey: 'participants',
      header: () => <TranscriptsColumnsHeaderButton text={lang.get('msg.participant')} />,
      cell: ({ row }) => {
        const participantLabel = row?.original?.respondent_index != null
          ? `${lang.get('msg.participant')} ${row.original.respondent_index}`
          : `${lang.get('msg.participant')} ${(row?.index ?? 0) + 1}`;

        if (isScreenedOut) {
          return (
            <div className="flex items-center h-full gap-3 whitespace-nowrap">
              <div className="flex items-center gap-3 font-semibold">
                <div style={{ color: getColor(row) }}>
                  <UserFilledIcon className="size-7" />
                </div>
                <p>{participantLabel}</p>
              </div>
            </div>
          );
        }

        return (
          <button
            type="button"
            className="flex items-center h-full gap-3 group whitespace-nowrap"
            onClick={() =>
              addQueryParams({
                selected_transcript: row?.original?.conversation_id ?? row?.original?._id,
              })
            }
          >
            <div className="flex items-center gap-3 font-semibold transition-all ease-in-out group-hover:underline">
              <div style={{ color: getColor(row) }}>
                <UserFilledIcon className="size-7" />
              </div>
              <p>{participantLabel}</p>
              <div className="flex items-center ml-2 gap-1.5 text-black/80 whitespace-nowrap">
                {row?.original?.messages?.some(
                  (item) => item?.text !== '' && item?.role === 'user',
                ) && <IconKeyboard size={24} />}
                {row?.original?.messages?.some(
                  (message) =>
                    message?.data?.some((dataItem: File) => dataItem.type === 'audio') &&
                    message.role === 'user',
                ) && <IconVolume size={24} />}
                {row?.original?.messages?.some(
                  (message) =>
                    message?.data?.some((dataItem: File) => dataItem.type === 'video') &&
                    message.role === 'user',
                ) && <IconVideo size={24} />}
              </div>
            </div>
          </button>
        );
      },
    },
    {
      accessorFn: (row) => row.conversation_id || row._id,
      id: 'id',
      header: ({ column }) => (
        <TranscriptsColumnsHeaderButton
          text={lang.get('msg.conversationID')}
          onClick={() => toggleSortHandler(column)}
        />
      ),
      cell: ({ row }) => (
        <TranscriptsColumnsCell
          text={row?.original?.conversation_id ?? row?.original?._id}
          onClick={isScreenedOut ? undefined :() =>
            addQueryParams({
              selected_transcript: row?.original?.conversation_id ?? row?.original?._id,
            })
          }
        />
      ),
    },
    {
      accessorKey: 'createdAt',
      header: ({ column }) => (
        <TranscriptsColumnsHeaderButton
          text={lang.get('msg.startTime')}
          onClick={() => toggleSortHandler(column)}
        />
      ),
      cell: ({ row }) => (
        <TranscriptsColumnsCell
          text={row?.getValue('createdAt')}
          onClick={isScreenedOut ? undefined : () => addQueryParams({ selected_transcript: row?.original?._id })}
        />
      ),
    },
    ...(activeView !== TranscriptsView.INCOMPLETE && !isScreenedOut
      ? [
          {
            accessorKey: 'duration' as const,
            header: ({ column }: { column: Column<AllTranscriptsProps> }) => (
              <TranscriptsColumnsHeaderButton
                text={lang.get('msg.duration')}
                onClick={() => toggleSortHandler(column)}
              />
            ),
            cell: ({ row }: { row: Row<AllTranscriptsProps> }) => (
              <TranscriptsColumnsCell
                text={row?.getValue('duration') !== 'NaN:NaN:NaN' ? row?.getValue('duration') : '/'}
                onClick={() => addQueryParams({ selected_transcript: row?.original?._id })}
              />
            ),
          },
        ]
      : []),
    ...(activeView !== TranscriptsView.INCOMPLETE && !isScreenedOut
      ? [
          {
            accessorFn: (row: AllTranscriptsProps) => row?.digest?.summary || '',
            id: 'summary',
            header: ({ column }: { column: Column<AllTranscriptsProps> }) => (
              <TranscriptsColumnsHeaderButton
                text={lang.get('msg.summary')}
                onClick={() => toggleSortHandler(column)}
              />
            ),
            cell: ({ row }: { row: Row<AllTranscriptsProps> }) => {
              const rawSummary = row?.original?.digest?.summary || '/';
              const plainTextSummary = rawSummary === '/' ? '/' : stripMarkdown(rawSummary);

              return (
                <TooltipWrapper
                  text={plainTextSummary}
                  className="max-w-[700px] text-xs z-[9999999]"
                >
                  <p className="w-[500px] truncate">{plainTextSummary}</p>
                </TooltipWrapper>
              );
            },
          },
        ]
      : []),
  ];

  if (metadataKeysArray.length) {
    metadataKeysArray.forEach((key: string) => {
      columns.push({
        accessorKey: `metadata.${key}`,
        id: `metadata.${key}`,
        header: ({ column }) => (
          <TranscriptsColumnsHeaderButton
            text={replaceUnderscoreWithSpace(key[0].toUpperCase() + key.slice(1))}
            onClick={() => toggleSortHandler(column)}
          />
        ),
        cell: ({ row }) => (
          <TranscriptsColumnsCell
            text={row.original.metadata?.[key] || '/'}
            onClick={() => addQueryParams({ selected_transcript: row?.original?._id })}
            additionalClasses="flex items-center justify-start w-full h-full"
          />
        ),
      });
    });
  }

  return columns;
};

export default TranscriptsColumns;
